import { Trans, useLingui } from '@lingui/react/macro';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import { Location } from 'modules/locations/domain/interfaces/location';
import { useLocationList } from 'modules/locations/ui/hooks/use-location-list';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UDText } from 'ud-ui/components';
import UDButton from 'ud-ui/components/button';
import HeaderContentCategory from 'ud-ui/components/category/components/list-wrapper/header-content';
import { mapAddressToDadataArea } from 'ud-ui/components/dadata-address-autocomplete/infra/data-mappers/dadata-area.data-mapper';
import StadiumPlaceholderIcon from 'ud-ui/components/icon/stadium-placeholder-icon';
import Modal from 'ud-ui/components/modal';
import { UDEntityRow, UDPagedList } from 'ud-ui/components/paged-list';

/** Пояснение из прежнего `EmptyState`: пустой список — единственное место, где его читают. */
const EMPTY_SUBTITLE = (
  <UDText variant="text-2" className="mb-6 max-w-200 text-center">
    <Trans>
      Локация — это конкретное место, где проводится турнир или отдельный матч. В мобильном приложении участники и зрители увидят не
      только название локации, но и её детальное описание: точный адрес, особенности инфраструктуры (например, раздевалки, парковка,
      зоны для зрителей). Чем полнее и точнее вы заполните информацию о месте, тем более привлекательным будет Ваш турнир.
    </Trans>
  </UDText>
);

function LocationsListPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { setHeaderContent } = useMainLayout();

  const createButtonText = t`Добавить локацию`;

  const handleCreateClick = useCallback(() => {
    navigate(Pages.LOCATIONS.CREATE);
  }, [navigate]);

  const {
    items,
    itemsOffset,
    state,
    page,
    totalPages,
    perPage,
    draftSearch,
    setPage,
    setPerPage,
    setSearch,
    buildPageHref,
    removeLocation,
  } = useLocationList();

  const [locationToDelete, setLocationToDelete] = useState<Location | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = useCallback((location: Location) => {
    setLocationToDelete(location);
  }, []);
  const closeDelete = useCallback(() => {
    setLocationToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!locationToDelete) return;
    setIsDeleting(true);
    try {
      await removeLocation(locationToDelete.id);
      setLocationToDelete(null);
      toast.success(t`Локация успешно удалена`);
    } catch {
      toast.error(t`Не удалось удалить локацию, возможно она используется в турнирах/матчах`);
    } finally {
      setIsDeleting(false);
    }
  }, [locationToDelete, removeLocation, t]);

  const handleLocationClick = useCallback(
    (location: Location) => {
      navigate(Pages.LOCATIONS.builders.edit(location.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(
      <HeaderContentCategory title={t`Локации`} createButtonText={createButtonText} createButtonOnClick={handleCreateClick} />,
    );
    return () => setHeaderContent(null);
  }, [createButtonText, handleCreateClick, setHeaderContent, t]);

  // Номер строки продолжается со страницы на страницу и считается от `itemsOffset`,
  // а не от `page`: пока едет следующая страница, на экране ещё прошлые элементы.
  const renderLocation = useCallback(
    (location: Location, index: number) => {
      const address = location.address ? mapAddressToDadataArea(location.address).value : '';

      return (
        <UDEntityRow
          index={itemsOffset + index + 1}
          avatar={location.images[0]?.externalUrl ?? <StadiumPlaceholderIcon size={52} className="text-surfaceRomanSilver30" />}
          title={location.name}
          subtitle={
            <span className="block overflow-hidden text-ellipsis whitespace-nowrap" title={address}>
              {address}
            </span>
          }
          actions={
            <UDButton variant="secondary" style={{ width: 144 }} onClick={() => openDelete(location)}>
              <Trans>Удалить</Trans>
            </UDButton>
          }
          onClick={() => handleLocationClick(location)}
        />
      );
    },
    [handleLocationClick, itemsOffset, openDelete],
  );

  const locationToDeleteName = locationToDelete?.name ?? '';

  return (
    <>
      <UDPagedList<Location>
        items={items}
        renderItem={renderLocation}
        getItemKey={(location) => location.id}
        state={state}
        page={page}
        totalPages={totalPages}
        perPage={perPage}
        onPageChange={setPage}
        onPerPageChange={setPerPage}
        pageHref={buildPageHref}
        search={draftSearch}
        onSearchChange={setSearch}
        searchPlaceholder={t`Поиск по названию локации`}
        emptyText={t`У Вас пока нет добавленных локаций`}
        emptySubtitle={EMPTY_SUBTITLE}
        emptyAction={
          <UDButton variant="primary" onClick={handleCreateClick}>
            {createButtonText}
          </UDButton>
        }
        notFoundText={t`Локации не найдены`}
      />

      <Modal.Confirm
        show={Boolean(locationToDelete)}
        title={t`Удалить локацию «${locationToDeleteName}»?`}
        description={t`Это действие необратимо.`}
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={closeDelete}
      />
    </>
  );
}

export default LocationsListPage;
